import { cx } from '@codegouvfr/react-dsfr/fr/cx';
import Input from '@codegouvfr/react-dsfr/Input';
import { DepartmentLabels } from 'maestro-shared/referential/Department';
import { LegalContextLabels } from 'maestro-shared/referential/LegalContext';
import { companyWorkshopLabel } from 'maestro-shared/schema/Company/CompanyWorkshop';
import type { Geolocation } from 'maestro-shared/schema/Geolocation/Geolocation';
import { ContextLabels } from 'maestro-shared/schema/ProgrammingPlan/Context';
import { programmingPlanContexts } from 'maestro-shared/schema/ProgrammingPlan/ProgrammingPlans';
import {
  isPPVSubPlan,
  subPlanLabel
} from 'maestro-shared/schema/ProgrammingPlan/ProgrammingSubPlan';
import {
  isCreatedPartialSample,
  type SampleChecked,
  type SampleOwnerData,
  type SampleToCreate
} from 'maestro-shared/schema/Sample/Sample';
import { formatMaestroDate } from 'maestro-shared/utils/date';
import SampleGeolocationForm from 'src/components/Sample/SampleGeolocationForm/SampleGeolocationForm';
import { useAuthentication } from 'src/hooks/useAuthentication';
import type { UseForm } from 'src/hooks/useForm';
import { useMatrixReferential } from 'src/hooks/useMatrixReferential';
import { quote } from 'src/utils/stringUtils';
import StepSummary, {
  type StepSummaryMode
} from 'src/views/SampleView/StepSummary/StepSummary';
import { SampleMap } from '../../../components/Sample/SampleMap/SampleMap';
import { usePartialSample } from '../../../hooks/usePartialSample';

interface Props {
  sample: (SampleChecked | SampleToCreate) & Partial<SampleOwnerData>;
  mode?: StepSummaryMode;
  onChangeResytalId: (resytalId: string) => void;
  onEdit?: () => void;
  geolocationX?: number;
  geolocationY?: number;
  onChangeLocation?: (location: Geolocation) => void;
  onChangeGeolocationX?: (x: number) => void;
  onChangeGeolocationY?: (y: number) => void;
  geolocationForm?: UseForm<any>;
}

const ContextStepSummary = ({
  sample,
  mode = 'section',
  onChangeResytalId,
  onEdit,
  geolocationX,
  geolocationY,
  onChangeLocation,
  onChangeGeolocationX,
  onChangeGeolocationY,
  geolocationForm
}: Props) => {
  const { user } = useAuthentication();
  const { readonly, programmingPlan, programmingSubPlan } =
    usePartialSample(sample);
  const matrixReferential = useMatrixReferential();

  return (
    <StepSummary title="Contexte du prélèvement" onEdit={onEdit} mode={mode}>
      <div className="summary-item icon-text">
        <div className={cx('fr-icon-user-line')}></div>
        <div>
          Prélèvement réalisé par{' '}
          <b>
            {isCreatedPartialSample(sample)
              ? `${sample.sampler.name}`
              : `${user?.name}`}
          </b>
          {isCreatedPartialSample(sample) && sample.additionalSampler && (
            <>
              {' et '}
              <b>{sample.additionalSampler.name}</b>
            </>
          )}
        </div>
      </div>
      {mode === 'tab' && isCreatedPartialSample(sample) && (
        <div className="summary-item icon-text">
          <div className={cx('fr-icon-calendar-event-line')}></div>
          <div>
            Date et heure du prélèvement :{' '}
            <b>
              {formatMaestroDate(sample.sampledDate)} à {sample.sampledTime}
            </b>
          </div>
        </div>
      )}
      <div className="summary-item icon-text">
        <div className={cx('fr-icon-road-map-line')}></div>
        <div className="flex-grow-1">
          {sample.department ? (
            <>
              Département : <b>{DepartmentLabels[sample.department]}</b>
            </>
          ) : (
            <div>
              Département :{' '}
              <span className="missing-data">Informations à compléter</span>
            </div>
          )}
          {onChangeLocation &&
          onChangeGeolocationX &&
          onChangeGeolocationY &&
          geolocationForm ? (
            <SampleGeolocationForm
              title="Emplacement de la parcelle contrôlée"
              geolocationX={geolocationX}
              geolocationY={geolocationY}
              onChangeLocation={onChangeLocation}
              onChangeGeolocationX={onChangeGeolocationX}
              onChangeGeolocationY={onChangeGeolocationY}
              inputForm={geolocationForm}
              readonly={false}
            />
          ) : sample.geolocation ? (
            <div>
              <div>
                Latitude : <b>{sample.geolocation.x}</b> Longitude :
                <b>{sample.geolocation.y}</b>
              </div>
              <SampleMap
                markerX={sample.geolocation.x}
                markerY={sample.geolocation.y}
                fullWidth
              />
            </div>
          ) : (
            <div>
              Latitude et longitude :{' '}
              <span className="missing-data">Informations à compléter</span>
            </div>
          )}
          {sample.parcel && (
            <div>
              N° ou appellation de la parcelle : <b>{sample.parcel}</b>
            </div>
          )}
        </div>
      </div>
      {programmingPlanContexts({ subPlans: programmingPlan?.subPlans ?? [] })
        .length > 1 && (
        <div className="summary-item icon-text">
          <div className={cx('fr-icon-microscope-line')}></div>
          <div>
            Contexte : <b>{ContextLabels[sample.context]}</b>
          </div>
        </div>
      )}
      {(programmingPlan?.subPlans ?? []).length > 1 && (
        <div className="summary-item icon-text">
          <div className={cx('fr-icon-microscope-line')}></div>
          <div>
            Type de plan :{' '}
            <b>
              {programmingSubPlan
                ? subPlanLabel(matrixReferential, programmingSubPlan)
                : ''}
            </b>
          </div>
        </div>
      )}
      {sample.legalContext && isPPVSubPlan(programmingSubPlan) && (
        <div className="summary-item icon-text">
          <div className={cx('fr-icon-scales-3-line')}></div>
          <div>
            Cadre juridique : <b>{LegalContextLabels[sample.legalContext]}</b>
          </div>
        </div>
      )}
      <div className="summary-item icon-text">
        <div className={cx('fr-icon-map-pin-2-line')}></div>
        <div>
          Entité contrôlée :{' '}
          {sample.company ? (
            <>
              <b>{sample.company.name}</b> - SIRET {sample.company.siret}
            </>
          ) : (
            <>
              {sample.companyOffline}
              <div className="missing-data">Information à compléter</div>
            </>
          )}
        </div>
      </div>
      {sample.companyWorkshop && (
        <div className="summary-item icon-text">
          <div className={cx('fr-icon-building-line')}></div>
          <div>
            Atelier : <b>{companyWorkshopLabel(sample.companyWorkshop)}</b>
          </div>
        </div>
      )}
      {isPPVSubPlan(programmingSubPlan) && (
        <div className="summary-item icon-text">
          <div className={cx('fr-icon-map-pin-user-line')}></div>
          <div>
            <Input
              label="Identifiant Resytal"
              hintText="Format AA-XXXXXX"
              nativeInputProps={{
                defaultValue: sample.resytalId || '',
                onChange: (e) => onChangeResytalId?.(e.target.value)
              }}
              disabled={readonly}
            />
          </div>
        </div>
      )}
      {sample.notesOnCreation && (
        <div className="summary-item icon-text">
          <div className={cx('fr-icon-quote-line')}></div>
          <div>
            Note additionnelle{' '}
            <div>
              <b>{quote(sample.notesOnCreation)}</b>
            </div>
          </div>
        </div>
      )}
    </StepSummary>
  );
};

export default ContextStepSummary;
